import { Suspense } from 'react';
import {
  Route,
  createBrowserRouter,
  createRoutesFromElements,
} from 'react-router-dom';
import { Loadable } from 'components/atoms/Loadable';
import { PrivateRoute } from 'components/routes/PrivateRoute';
import { NonUserRoute } from '../NonUserRoute';
import { GuestSessionGate } from '../GuestSessionGate';
import FullLoader from 'components/Loader/FullLoader';
import {
  ADMIN_INITIAL_SETUP_PATH,
  ADMIN_ONBOARDING_PENDING_PATH,
} from 'pages/Onboarding/constants/constants';
import { OnboardingGuard } from 'components/routes/OnboardingGuard';
import { OnboardingPendingGuard } from 'components/routes/OnboardingPendingGuard';

const LoginPage = Loadable({
  loader: () => import('pages/Main/Login'),
});

const ChangePasswordByUserPage = Loadable({
  loader: () => import('pages/Main/ChangePasswordByUser'),
});

const ForgotPasswordPage = Loadable({
  loader: () => import('pages/Main/ForgotPassword'),
});

const XeroSignUpPage = Loadable({
  loader: () => import('pages/Main/XeroSignUp'),
});

const ViewPortalPage = Loadable({
  loader: () =>
    import('pages/ProjectBudget/DayworkDocket/ClientApprovalTemplates/ViewPortal'),
});

const SignPortalPage = Loadable({
  loader: () =>
    import('pages/ProjectBudget/DayworkDocket/ClientApprovalTemplates/SignPortal'),
});

const QRScreensPage = Loadable({
  loader: () => import('pages/Main/QRScreens'),
});

const QRPages = Loadable({
  loader: () =>
    import('pages/Main/QRPages').then((module) => ({
      default: module.QRPages,
    })),
});

const DashboardPage = Loadable({
  loader: () => import('../../../pages/Home'),
});

const AdminInitialSetupPage = Loadable({
  loader: () =>
    import('pages/Onboarding/containers/AdminInitialSetup').then((module) => ({
      default: module.AdminInitialSetupPage,
    })),
  fallback: <FullLoader />,
});

const OnboardingPendingPage = Loadable({
  loader: () =>
    import('pages/Onboarding/containers/OnboardingPendingPage/OnboardingPendingPage'),
  fallback: <FullLoader />,
});

export const router = createBrowserRouter(
  createRoutesFromElements(
    <>
      <Route path="/login" element={<NonUserRoute />}>
        <Route path="/login" element={<LoginPage />} />
      </Route>
      <Route path="/change-password" element={<NonUserRoute />}>
        <Route path="/change-password" element={<ChangePasswordByUserPage />} />
      </Route>
      <Route path="/forgot-password" element={<NonUserRoute />}>
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      </Route>

      <Route path="/xerosignup" element={<NonUserRoute />}>
        <Route path="/xerosignup" element={<XeroSignUpPage />} />
      </Route>
      <Route path="/daywork-docket/client-signature" element={<NonUserRoute />}>
        <Route
          path="/daywork-docket/client-signature"
          element={<ViewPortalPage />}
        />
      </Route>
      <Route path="/access-daywork" element={<NonUserRoute />}>
        <Route path="/access-daywork" element={<SignPortalPage />} />
      </Route>
      {/*
       * Guest-facing QR routes.
       *
       * These are the only routes where a guest user (user_type = GUEST /
       * NEW_GUEST) can authenticate and interact with the app
       *
       * Rendering order for `/forms-qr/*` and `/qr/*`:
       *   GuestSessionGate  -> restores tokens on reload (sync) + tracks tabs
       *     NonUserRoute    -> runs useAuth; routes guest vs. non-guest users
       *       QRScreensPage / QRPages -> the actual guest UI
       */}
      <Route element={<GuestSessionGate />}>
        <Route path="/forms-qr/*" element={<NonUserRoute />}>
          <Route path="*" element={<QRScreensPage />} />
        </Route>
        <Route path="/qr/*" element={<NonUserRoute />}>
          <Route path="*" element={<QRPages />} />
        </Route>
      </Route>

      <Route path={ADMIN_INITIAL_SETUP_PATH} element={<PrivateRoute />}>
        <Route
          index
          element={
            <OnboardingGuard requireCompleted={false}>
              <AdminInitialSetupPage />
            </OnboardingGuard>
          }
        />
      </Route>

      <Route path={ADMIN_ONBOARDING_PENDING_PATH} element={<PrivateRoute />}>
        <Route
          index
          element={
            <OnboardingPendingGuard>
              <OnboardingPendingPage />
            </OnboardingPendingGuard>
          }
        />
      </Route>

      <Route path="*" element={<PrivateRoute />}>
        <Route
          path="*"
          element={
            <Suspense fallback={<FullLoader />}>
              <OnboardingGuard requireCompleted={true}>
                <DashboardPage />
              </OnboardingGuard>
            </Suspense>
          }
        />
      </Route>
    </>,
  ),
);
